import React from "react";
import { DashboardChart } from "./chart";
import { SelectPicker } from "rsuite";
import { months } from "../../../shared/constants/months";
import * as Styled from "./styles";
import { useFileFilters } from "modules/files/providers/FileFilters";
import { ICompany, ICompanyDashboardItem } from "modules/companies/types";
import { getDashboard } from "modules/companies/services";
import { Button, CircularProgress } from "@mui/material";
import { Checkbox } from "rsuite";
import { useIntl, defineMessages } from "react-intl";

interface IKeyValue {
  label: string;
  value: number;
}

interface IFilters {
  companyId: number;
  month: string;
  deleted?: boolean;
}

const messages = defineMessages({
  title: { id: "dashboard.title" },
  company: { id: "company" },
  filterByCompany: { id: "dashboard.filter.by.company" },
  showDeletedRows: { id: "dashboard.show.deleted.rows" },
  filterByMonth: { id: "dashboard.filter.by.month" },
  wait: { id: "loading" },
  month: { id: "month" },
});

const DashboardReport: React.FC = () => {
  const { companies, setShowDeletedCompanies } = useFileFilters();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [loadingDownload, setLoadingDownload] = React.useState<boolean>(false);
  const [filters, setFilters] = React.useState<IFilters>({} as IFilters);
  const [localCompanies, setLocalCompanies] = React.useState<IKeyValue[]>([]);
  const [data, setData] = React.useState<ICompanyDashboardItem>({
    days: [],
    gb: [],
    files: [],
  });

  const handleData = React.useCallback(
    async (download: boolean = false) => {
      console.log(filters.companyId, filters.month);
      if (!filters.companyId || !filters.month) {
        setLoading(false);
        setLoadingDownload(false);
        return false;
      }

      if (!download) {
        setLoading(true);
      } else {
        setLoadingDownload(true);
      }
      const { status, data: dataDashboard } = await getDashboard({
        ...filters,
        download,
      });

      if ([200, 201].includes(status)) {
        if (download) {
          const fileName = `Relatorio_${
            filters.month
          }_${new Date().getFullYear()}.xlsx`;

          const url = URL.createObjectURL(new Blob([dataDashboard]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();

          URL.revokeObjectURL(link.href);
          setLoadingDownload(false);
        } else {
          setData(dataDashboard);
          setLoading(false);
        }
      }
    },
    [filters]
  );

  React.useEffect(() => {
    const cm: IKeyValue[] = [];
    if (companies && companies.length > 0) {
      companies.forEach((company: ICompany, index: number) => {
        if (index === 0) {
          setFilters({
            companyId: company.id,
            month: (new Date().getMonth() + 1).toString(),
          });
        }
        cm.push({ label: company.fantasy_name, value: company.id || 0 });
      });
    }
    setLocalCompanies(cm);
  }, [companies, setShowDeletedCompanies]);

  React.useEffect(() => {
    handleData();
  }, [handleData]);

  return (
    <Styled.Container>
      <h4>{formatMessage(messages.title)}</h4>
      <Styled.Header>
        <div style={{ width: "100%", marginRight: 10 }}>
          <label>
            <b>{formatMessage(messages.company)}</b>
          </label>
          <SelectPicker
            data={localCompanies}
            placeholder={formatMessage(messages.filterByCompany)}
            style={{ width: "100%" }}
            onChange={(companyId: number) =>
              setFilters({ ...filters, companyId })
            }
          />
          <Checkbox
            onChange={(value, checked) => {
              //setFilters({ ...filters, deleted: checked });
              setShowDeletedCompanies(checked);
            }}
          >
            {formatMessage(messages.showDeletedRows)}
          </Checkbox>
        </div>
        <div style={{ width: "25%", marginRight: 10 }}>
          <label>
            <b>{formatMessage(messages.month)}</b>
          </label>
          <SelectPicker
            data={months}
            placeholder={formatMessage(messages.filterByMonth)}
            style={{ width: "100%" }}
            onChange={(month: string) => setFilters({ ...filters, month })}
          />
        </div>
        <div
          style={{
            width: "25%",
            marginRight: 10,
            marginTop: 23,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => handleData(true)}
            disabled={
              loading || loadingDownload || !filters.companyId || !filters.month
            }
          >
            {loadingDownload ? <CircularProgress size={24} /> : "Baixar"}
          </Button>
        </div>
        <div
          style={{
            width: "100%",
            marginRight: 10,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            paddingTop: "20px",
          }}
        >
          {loading && (
            <>
              <CircularProgress size={15} />
              {formatMessage(messages.wait)}
            </>
          )}
        </div>
      </Styled.Header>
      <Styled.Chart>
        {!loading && (
          <div style={{ display: "flex", width: "100%" }}>
            <DashboardChart values={data} />
          </div>
        )}
      </Styled.Chart>
    </Styled.Container>
  );
};

export { DashboardReport };
